// A machine's meta-kit machines.yaml in its Fleet card (P-020): its state from the Fleet poll,
// and on Open the file itself (GET …/machines-file). Since P-021 owners edit it in a form (every
// machines.schema field per machine, set or not, defaults greyed, each with an (i) tooltip) or as
// Raw YAML. The form sends only its changes, which the board applies to the file in place so its
// comments stay; Raw YAML sends the whole text. Either way the board validates (refusals beside
// their field) and queues a `machines-file` command the host carries out if its file has not
// changed. Switching Form ↔ Raw YAML carries the draft across through the board's preview.
// Owners can also copy the file to the same developer's other machines on this board.
// Hook class: machines-file.
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage, type FleetCommand, type FleetHost, type FleetMachinesFileDoc } from '../api/client';
import { setting } from '../lib/settings';
import { useSession } from '../lib/session';
import { ago } from '../lib/time';
import { usePhone } from '../lib/usePhone';
import { SettingLabel, SettingTip } from './SettingsFields';
import { Badge, Button, ConfirmDialog, Input, TabPanel, Tabs, TagInput, Textarea, cn, mono } from './ui';

const SECTION_HEAD = 'flex items-baseline gap-1.5 px-4 pb-1.5 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted';
const META = 'text-[11.5px] text-ink-muted';
const FIELD_ERROR = 'm-0 text-[11.5px] text-danger-ink';

export const TOO_OLD = 'Reporter too old to read the machines file';

function short(sha: string | null): string {
  return sha ? sha.slice(0, 7) : '';
}

type Parsed = NonNullable<FleetMachinesFileDoc['parsed']>;
type Field = 'limit' | 'has' | 'protect' | 'min_free_gb' | 'hostname';
const FIELDS: Field[] = ['limit', 'has', 'protect', 'min_free_gb', 'hostname'];
interface Row {
  id: number;
  /** The name in the file; null for a machine the form added. */
  orig: string | null;
  name: string;
  limit: string;
  has: string[];
  protect: string[];
  min_free_gb: string;
  hostname: string;
}
interface Form {
  developer: string;
  machines: Row[];
}
type Change = { path: string[]; value?: unknown; rename?: string };

let nextId = 1;
function fromParsed(p: Parsed): Form {
  return {
    developer: p.developer ?? '',
    machines: p.machines.map((m) => ({
      id: nextId++,
      orig: m.name,
      name: m.name,
      limit: m.limit === null ? '' : String(m.limit),
      has: m.has,
      protect: m.protect,
      min_free_gb: m.min_free_gb === null ? '' : String(m.min_free_gb),
      hostname: m.hostname ?? '',
    })),
  };
}
function emptyRow(name: string, limit = ''): Row {
  return { id: nextId++, orig: null, name, limit, has: [], protect: [], min_free_gb: '', hostname: '' };
}

/** A field's value as the file holds it; null is unset (the key goes). */
function fieldValue(row: Row, f: Field): unknown {
  if (f === 'has' || f === 'protect') return row[f].length ? row[f] : null;
  if (f === 'hostname') return row.hostname.trim() || null;
  const text = row[f].trim();
  return text === '' ? null : Number(text);
}

/** The form's edits, in the API's shape: removals, then renames, then field values. */
function diff(base: Form, form: Form): Change[] {
  const out: Change[] = [];
  if (form.developer.trim() !== base.developer) out.push({ path: ['developer'], value: form.developer.trim() || null });
  const kept = new Set(form.machines.map((r) => r.orig).filter((n): n is string => n !== null));
  for (const r of base.machines) if (r.orig !== null && !kept.has(r.orig)) out.push({ path: ['machines', r.orig], value: null });
  for (const r of form.machines) if (r.orig !== null && r.name.trim() !== r.orig) out.push({ path: ['machines', r.orig], rename: r.name.trim() });
  for (const r of form.machines) {
    const was = base.machines.find((b) => b.orig !== null && b.orig === r.orig);
    for (const f of FIELDS) {
      const v = fieldValue(r, f);
      if (JSON.stringify(v) !== JSON.stringify(was ? fieldValue(was, f) : null)) out.push({ path: ['machines', r.name.trim(), f], value: v });
    }
  }
  return out;
}

const PLAIN = /^[A-Za-z0-9_][A-Za-z0-9_.@/+~-]*$/;
const RESERVED = /^(true|false|yes|no|on|off|null|~|[-+]?[0-9][0-9_.:eE+-]*)$/i;
const yamlText = (s: string) => (PLAIN.test(s) && !RESERVED.test(s) ? s : JSON.stringify(s));

/** A new file from the form, for a host that has none yet. */
function serialise(form: Form): string {
  const lines = form.developer.trim() ? [`developer: ${yamlText(form.developer.trim())}`] : [];
  lines.push(form.machines.length ? 'machines:' : 'machines: {}');
  for (const r of form.machines) {
    lines.push(`  ${yamlText(r.name.trim())}:`);
    for (const f of FIELDS) {
      const v = fieldValue(r, f);
      if (v === null) continue;
      lines.push(`    ${f}: ${Array.isArray(v) ? `[${v.map(yamlText).join(', ')}]` : typeof v === 'string' ? yamlText(v) : String(v)}`);
    }
  }
  return `${lines.join('\n')}\n`;
}

type Errors = Record<string, string>;
const errorKey = (row: Row, f: Field | 'name') => `${row.id}.${f}`;

/** What the host's reader would refuse, found before the board is asked. */
function check(form: Form): Errors {
  const out: Errors = {};
  if (form.developer.trim() && !/^[A-Za-z0-9-]+$/.test(form.developer.trim())) out.developer = 'A GitHub login: letters, digits and hyphens.';
  const names = form.machines.map((r) => r.name.trim());
  for (const r of form.machines) {
    const name = r.name.trim();
    if (!name) out[errorKey(r, 'name')] = 'Name the machine.';
    else if (names.filter((n) => n === name).length > 1) out[errorKey(r, 'name')] = 'Another machine has this name.';
    for (const f of ['limit', 'min_free_gb'] as const) {
      if (r[f].trim() && !/^\d+$/.test(r[f].trim())) out[errorKey(r, f)] = 'A whole number, 0 or more.';
    }
    const bad = r.has.find((h) => !/^[A-Za-z0-9._-]+$/.test(h));
    if (bad) out[errorKey(r, 'has')] = `${bad}: letters, digits, dots, hyphens and underscores only.`;
    const onlyOthers = !r.has.length && (r.protect.length || r.min_free_gb.trim() || r.hostname.trim());
    if (!r.limit.trim() && !onlyOthers) out[errorKey(r, 'limit')] = 'Set a limit (or only protect, min free GB or hostname).';
  }
  return out;
}

/** The board's refusals, by field where the error names one (`path`). */
function fromServer(error: unknown, form: Form): { fields: Errors; general: string[] } {
  const fields: Errors = {};
  const general: string[] = [];
  const list = (error as { errors?: { line: number; message: string; path?: string[] }[] } | undefined)?.errors ?? [];
  for (const e of list) {
    const [top, name, f] = e.path ?? [];
    const row = top === 'machines' ? form.machines.find((r) => r.name.trim() === name) : undefined;
    const field = f && (FIELDS as string[]).includes(f) ? (f as Field) : undefined;
    if (top === 'developer') fields.developer = `Line ${e.line}: ${e.message}`;
    else if (row) fields[errorKey(row, field ?? 'name')] = `Line ${e.line}: ${e.message}`;
    else general.push(`Line ${e.line}: ${e.message}`);
  }
  return { fields, general };
}

function WriteLine({ cmd, host, now }: { cmd: FleetCommand; host: string; now: number }) {
  const when = cmd.result_at ? ` · ${ago(cmd.result_at, now)}` : '';
  const by = cmd.by ? ` by ${cmd.by}` : '';
  switch (cmd.state) {
    case 'queued':
    case 'picked':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="status">
          <Badge tone="signal" spinner>Writing</Badge>
          <span className="text-ink-muted">{host} writes the file as its Orca user, if it has not changed since.</span>
        </p>
      );
    case 'ok':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="status">
          <Badge tone="success">Written</Badge>
          <span className={cn(mono, 'text-[11.5px] text-ink-muted')}>
            {short(cmd.sha256)}
            {when}
            {by}
          </span>
        </p>
      );
    case 'failed':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="alert">
          <Badge tone="danger">Write refused</Badge>
          <span className="whitespace-pre-wrap text-danger-ink">
            {cmd.error === 'changed' ? `The file on ${host} changed before the write; reload and edit again.` : cmd.error}
          </span>
        </p>
      );
    default:
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="alert">
          <Badge tone="warning">{cmd.state === 'expired' ? 'Not picked up' : 'No answer'}</Badge>
          <span className="text-ink-muted">{host} did not write the file.</span>
        </p>
      );
  }
}

/** One machine's fields, as a table row (desktop) or a card (phone). */
function MachineFields({
  row,
  self,
  phone,
  disabled,
  errors,
  update,
  remove,
}: {
  row: Row;
  self: boolean;
  phone: boolean;
  disabled: boolean;
  errors: Errors;
  update: (patch: Partial<Row>) => void;
  remove: () => void;
}) {
  const name = row.name.trim() || 'new machine';
  const err = (f: Field | 'name') => errors[errorKey(row, f)];
  const note = (f: Field | 'name') => err(f) && <p className={FIELD_ERROR} role="alert">{err(f)}</p>;
  const number = (f: 'limit' | 'min_free_gb', placeholder: string) => (
    <>
      <Input aria-label={`${setting(`machines.<name>.${f}`).label} · ${name}`} aria-invalid={!!err(f) || undefined} inputMode="numeric" className={cn('h-[30px] w-[80px]', err(f) && 'border-danger')} value={row[f]} placeholder={placeholder} disabled={disabled} onChange={(e) => update({ [f]: e.target.value })} />
      {note(f)}
    </>
  );
  const cells: Record<Field | 'name', ReactNode> = {
    name: (
      <>
        <div className="flex flex-wrap items-center gap-1.5">
          <Input aria-label={`Machine name · ${row.orig ?? 'new'}`} aria-invalid={!!err('name') || undefined} className={cn('h-[30px] min-w-[140px] flex-1', err('name') && 'border-danger')} value={row.name} disabled={disabled} onChange={(e) => update({ name: e.target.value })} />
          {self && <Badge tone="signal">This machine</Badge>}
        </div>
        {note('name')}
      </>
    ),
    limit: number('limit', '–'),
    has: (
      <>
        <TagInput label={`has · ${name}`} value={row.has} disabled={disabled} invalid={!!err('has')} placeholder="none" onChange={(has) => update({ has })} />
        {note('has')}
      </>
    ),
    protect: (
      <>
        <TagInput label={`protect · ${name}`} value={row.protect} disabled={disabled} placeholder="none" onChange={(protect) => update({ protect })} />
        {note('protect')}
      </>
    ),
    min_free_gb: number('min_free_gb', '10'),
    hostname: (
      <>
        <Input aria-label={`Hostname · ${name}`} className="h-[30px] min-w-[120px]" value={row.hostname} placeholder={row.name.trim() || 'its name'} disabled={disabled} onChange={(e) => update({ hostname: e.target.value })} />
        {note('hostname')}
      </>
    ),
  };
  const removeButton = !disabled && (
    <Button size="sm" variant="ghost" aria-label={`Remove ${name}`} onClick={remove}>
      Remove
    </Button>
  );
  if (phone) {
    return (
      <div className="grid gap-2.5 rounded-lg border border-hairline bg-surface p-3" data-machine={row.name}>
        <div className="flex items-start gap-1.5">
          <div className="min-w-0 flex-1">{cells.name}</div>
          {removeButton}
        </div>
        <div className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-2 gap-y-2 text-[12px]">
          {FIELDS.map((f) => (
            <FieldCell key={f} f={f}>
              {cells[f]}
            </FieldCell>
          ))}
        </div>
      </div>
    );
  }
  return (
    <tr data-machine={row.name}>
      {(['name', ...FIELDS] as const).map((f) => (
        <td key={f} className="border-b border-hairline px-2.5 py-2 align-top">
          <div className="grid gap-1">{cells[f]}</div>
        </td>
      ))}
      <td className="border-b border-hairline px-2.5 py-2 align-top">{removeButton}</td>
    </tr>
  );
}

function FieldCell({ f, children }: { f: Field; children: ReactNode }) {
  const s = setting(`machines.<name>.${f}`);
  return (
    <>
      <span className="flex items-center gap-1 text-ink-muted">
        {s.label}
        <SettingTip s={s} yamlKey={f} />
      </span>
      <div className="grid min-w-0 gap-1">{children}</div>
    </>
  );
}

export function MachinesFileSection({ host, ws, now, onChanged }: { host: FleetHost; ws: number; now: number; onChanged: () => void }) {
  const mf = host.machines_file;
  const { me } = useSession();
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState<FleetMachinesFileDoc | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState('form');
  // The form's base (the file as read, or Raw YAML's text after a switch) and the form itself.
  const [base, setBase] = useState<{ text: string | null; form: Form } | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [raw, setRaw] = useState('');
  // Raw YAML's text when it last matched the form: a draft that differs is read back on a switch.
  const [rawBase, setRawBase] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const path = { ws_id: ws, host_id: host.id };

  useEffect(() => {
    if (!open) return;
    let live = true;
    void api.GET('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/machines-file', { params: { path: { ws_id: ws, host_id: host.id } } }).then(({ data, error, response }) => {
      if (!live) return;
      if (data) {
        setDoc(data);
        setLoadError(null);
        start(data);
      } else setLoadError(errorMessage(error, response.status));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start() only sets state from the answer.
  }, [open, ws, host.id, mf.sha256, mf.state]);

  /** The form and Raw YAML as the file is, or for a host without one, a first machine. */
  function start(d: FleetMachinesFileDoc) {
    setErrors({});
    setGeneral(null);
    if (d.state === 'ok' && d.parsed) {
      const f = fromParsed(d.parsed);
      setBase({ text: null, form: f });
      setForm(f);
      syncRaw(d.content ?? '');
      setTab('form');
    } else if (d.state === 'none') {
      const f: Form = { developer: '', machines: [emptyRow(host.name, '2')] };
      setBase({ text: null, form: { developer: '', machines: [] } });
      setForm(f);
      syncRaw(serialise(f));
      setTab('form');
    } else {
      setBase(null);
      setForm(null);
      syncRaw(d.content ?? '');
      setTab('raw');
    }
  }
  function syncRaw(text: string) {
    setRaw(text);
    setRawBase(text);
  }

  if (mf.state === 'unknown' && !host.last_seen_at) return null;

  const fresh = doc?.state === 'none' && base?.text === null;
  const changes = base && form && !fresh ? diff(base.form, form) : [];
  const formDirty = fresh || changes.length > 0 || (base?.text ?? null) !== null;
  const rawDirty = raw !== rawBase || (doc?.state === 'none' && base?.text === null);
  const login = me.github.linked ? me.github.login : null;

  function showRefusal(error: unknown, status: number, f: Form | null) {
    const mapped = f ? fromServer(error, f) : { fields: {}, general: [] };
    setErrors(mapped.fields);
    const unplaced = mapped.general.length ? mapped.general.join('\n') : Object.keys(mapped.fields).length ? null : errorMessage(error, status);
    setGeneral(unplaced);
  }

  /** What the form asks of the board: its changes, on Raw YAML's text after a switch. */
  function formBody(f: Form) {
    if (fresh) return { content: serialise(f) };
    const body: { content?: string; changes?: Change[] } = {};
    if (base?.text !== null && base?.text !== undefined) body.content = base.text;
    const c = base ? diff(base.form, f) : [];
    if (c.length) body.changes = c;
    return body;
  }

  async function switchTab(next: string) {
    if (next === tab || !doc) return;
    setErrors({});
    setGeneral(null);
    if (next === 'raw' && form) {
      const found = check(form);
      if (Object.keys(found).length) return setErrors(found);
      const body = formBody(form);
      if (!body.changes && body.content === undefined) return setTab('raw');
      if (fresh || !body.changes) {
        if (body.content !== undefined) syncRaw(body.content);
        return setTab('raw');
      }
      setBusy(true);
      const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/machines-file/preview', { params: { path }, body });
      setBusy(false);
      if (!data) return showRefusal(error, response.status, form);
      syncRaw(data.content);
      if (data.parsed) {
        const f = fromParsed(data.parsed);
        setBase({ text: data.content, form: f });
        setForm(f);
      }
      return setTab('raw');
    }
    if (next === 'form') {
      if (raw === rawBase && form) return setTab('form');
      setBusy(true);
      const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/machines-file/preview', { params: { path }, body: { content: raw } });
      setBusy(false);
      if (!data || !data.parsed) return showRefusal(error, response.status, null);
      setRawBase(raw);
      const f = fromParsed(data.parsed);
      setBase({ text: data.content, form: f });
      setForm(f);
      setTab('form');
    }
  }

  async function save() {
    if (!doc) return;
    setErrors({});
    setGeneral(null);
    let body: { content?: string; changes?: Change[] };
    if (tab === 'form') {
      if (!form) return;
      const found = check(form);
      if (Object.keys(found).length) return setErrors(found);
      body = formBody(form);
    } else body = { content: raw };
    setBusy(true);
    const { data, error, response } = await api.PUT('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/machines-file', {
      params: { path },
      body: { ...body, expected_sha256: doc.sha256 },
    });
    setBusy(false);
    if (!data) return showRefusal(error, response.status, tab === 'form' ? form : null);
    onChanged();
  }

  async function copy() {
    if (!doc?.sha256) return;
    setBusy(true);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/machines-file/copy', {
      params: { path },
      body: { sha256: doc.sha256 },
    });
    setBusy(false);
    setCopying(false);
    if (!data) {
      setCopied(errorMessage(error, response.status));
      return;
    }
    const parts = [
      data.queued.length ? `Queued to ${data.queued.map((q) => q.host).join(', ')}.` : 'No machine needed it.',
      ...data.skipped.map((s) => `Skipped ${s.host}: ${s.reason}`),
    ];
    setCopied(parts.join(' '));
    onChanged();
  }

  const differing = doc?.peers.filter((p) => !p.same) ?? [];
  let status;
  switch (mf.state) {
    case 'ok':
      status = (
        <>
          <Badge tone="muted">machines.yaml</Badge>
          {mf.developer && <span className={META}>developer {mf.developer}</span>}
          <span className={cn(mono, 'text-[11px] text-ink-muted')}>{short(mf.sha256)}</span>
        </>
      );
      break;
    case 'none':
      status = <Badge tone="muted">No machines file</Badge>;
      break;
    case 'unreadable':
      status = <Badge tone="warning">Unreadable · {mf.reason ?? 'unknown reason'}</Badge>;
      break;
    case 'too_old':
      status = <Badge tone="muted">{TOO_OLD}</Badge>;
      break;
    default:
      status = <span className={META}>Not reported yet.</span>;
  }
  const canOpen = mf.state === 'ok' || mf.state === 'none';
  const editable = !!doc?.can_edit;
  const setRows = (machines: Row[]) => form && setForm({ ...form, machines });
  const devName = form?.developer.trim() ?? '';
  const isSelf = (r: Row) => (devName ? (r.hostname.trim() || r.name.trim()).toLowerCase() === host.name.toLowerCase() : r.name.trim() === 'local');
  const local = form?.machines.find((r) => r.name.trim() === 'local');
  const canRenameLocal = !!(editable && devName && local && !form?.machines.some((r) => r.name.trim() === host.name));
  const dirty = tab === 'form' ? formDirty : rawDirty;

  const formView = form && (
    <div className="grid min-w-0 gap-3.5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2 phone:grid-cols-1">
        <div className="grid min-w-0 gap-1">
          <SettingLabel path="developer" yamlKey="developer" htmlFor={`mf-${host.id}-developer`} />
          <Input id={`mf-${host.id}-developer`} aria-invalid={!!errors.developer || undefined} className={cn('h-[30px]', errors.developer && 'border-danger')} value={form.developer} placeholder="none" disabled={!editable} onChange={(e) => setForm({ ...form, developer: e.target.value })} />
          {errors.developer && <p className={FIELD_ERROR} role="alert">{errors.developer}</p>}
        </div>
        {editable && login && (
          <Button size="sm" disabled={form.developer.trim() === login} onClick={() => setForm({ ...form, developer: login })}>
            Use my login (@{login})
          </Button>
        )}
      </div>
      {canRenameLocal && local && (
        <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-lg border border-accent bg-accent-faded px-3 py-2.5 text-[12.5px] text-accent-display" role="status">
          <span>
            This file still names this machine <code className={mono}>local</code>. With a developer set, meta-kit expects its real name.
          </span>
          <Button size="sm" variant="primary" onClick={() => setRows(form.machines.map((r) => (r.id === local.id ? { ...r, name: host.name } : r)))}>
            Rename local → {host.name}
          </Button>
        </div>
      )}
      {phone ? (
        <div className="grid gap-2.5" aria-label="Machines">
          {form.machines.map((r) => (
            <MachineFields key={r.id} row={r} self={isSelf(r)} phone disabled={!editable} errors={errors} update={(patch) => setRows(form.machines.map((m) => (m.id === r.id ? { ...m, ...patch } : m)))} remove={() => setRows(form.machines.filter((m) => m.id !== r.id))} />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-hairline">
          <table className="w-full min-w-[760px] border-collapse text-[12.5px]" aria-label={`Machines in ${host.name}'s file`}>
            <thead>
              <tr>
                {(['name', ...FIELDS] as const).map((f, i) => {
                  const s = setting(f === 'name' ? 'machines.<name>' : `machines.<name>.${f}`);
                  return (
                    <th key={f} className="whitespace-nowrap border-b border-hairline bg-surface px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
                      <span className="inline-flex items-center gap-1">
                        {f === 'name' ? 'Machine' : s.label}
                        <SettingTip s={s} yamlKey={f === 'name' ? 'machines.<name>' : f} align={i > 3 ? 'end' : 'start'} />
                      </span>
                    </th>
                  );
                })}
                <th className="border-b border-hairline bg-surface" />
              </tr>
            </thead>
            <tbody>
              {form.machines.map((r) => (
                <MachineFields key={r.id} row={r} self={isSelf(r)} phone={false} disabled={!editable} errors={errors} update={(patch) => setRows(form.machines.map((m) => (m.id === r.id ? { ...m, ...patch } : m)))} remove={() => setRows(form.machines.filter((m) => m.id !== r.id))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setRows([...form.machines, emptyRow('')])}>
            + Add machine
          </Button>
          <span className={cn(META, 'ml-auto')}>Comments in the file are kept. Saved to this machine as the Orca user; refused if it changed since you opened it.</span>
        </div>
      )}
    </div>
  );

  return (
    <section className="machines-file" aria-label={`Machines file on ${host.name}`}>
      <h3 className={SECTION_HEAD}>Machines file</h3>
      <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
        {status}
        {mf.differs_from.length > 0 && <Badge tone="warning">Differs from {mf.differs_from.join(', ')}</Badge>}
        {canOpen && (
          <Button size="sm" className="ml-auto" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? 'Close' : mf.state === 'none' ? 'Open' : 'View'}
          </Button>
        )}
      </div>
      {mf.last_command && (
        <div className="px-4 pb-3">
          <WriteLine cmd={mf.last_command} host={host.name} now={now} />
        </div>
      )}
      {open && (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 border-t border-hairline px-4 py-3">
          {loadError && (
            <p className="m-0 text-[12px] text-danger-ink" role="alert">
              {loadError}
            </p>
          )}
          {doc && (
            <form
              className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn(mono, 'rounded border border-hairline-strong bg-surface px-2 py-0.5 text-[11.5px]')}>~/.config/meta-kit/machines.yaml</span>
                {doc.state === 'none' && <span className={META}>{host.name} has none yet for its Orca user.</span>}
                <Tabs
                  className="ml-auto"
                  label={`machines.yaml on ${host.name}`}
                  idPrefix={`mf-${host.id}`}
                  tabs={[
                    { id: 'form', label: 'Form' },
                    { id: 'raw', label: 'Raw YAML' },
                  ]}
                  value={tab}
                  onValueChange={(id) => void switchTab(id)}
                />
              </div>
              <TabPanel id={tab} idPrefix={`mf-${host.id}`}>
                {tab === 'form' ? (
                  formView ?? <p className={cn(META, 'm-0')}>The file does not validate, so it opens in Raw YAML.</p>
                ) : editable ? (
                  <Textarea aria-label={`machines.yaml on ${host.name}`} className="min-h-[260px] font-mono text-[12px]" value={raw} spellCheck={false} onChange={(e) => setRaw(e.target.value)} />
                ) : (
                  <pre className="m-0 max-h-[360px] overflow-auto rounded border border-hairline bg-surface-raised p-3 font-mono text-[12px] text-ink-display">{doc.content}</pre>
                )}
              </TabPanel>
              {general && (
                <p className="m-0 whitespace-pre-wrap text-[12px] text-danger-ink" role="alert">
                  {general}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {editable ? (
                  <>
                    <Button type="submit" variant="primary" size="sm" disabled={busy || !dirty}>
                      {busy ? 'Working' : `Save to ${host.name}`}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy || !dirty} onClick={() => start(doc)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <span className={META}>{doc.why_not}</span>
                )}
                {differing.length > 0 && !dirty && (
                  <Button size="sm" onClick={() => setCopying(true)} disabled={busy}>
                    Copy to {differing.map((p) => p.name).join(', ')}
                  </Button>
                )}
              </div>
              {copied && (
                <p className="m-0 text-[12px] text-ink-muted" role="status">
                  {copied}
                </p>
              )}
            </form>
          )}
        </div>
      )}
      {copying && doc && (
        <ConfirmDialog
          title={`Copy ${host.name}'s machines file?`}
          confirmText={host.name}
          action="Copy machines file"
          danger={false}
          busy={busy}
          onCancel={() => setCopying(false)}
          onConfirm={() => void copy()}
        >
          <p className="m-0">
            {differing.map((p) => p.name).join(', ')} {differing.length === 1 ? 'gets' : 'get'} this file in place of {differing.length === 1 ? 'its own' : 'their own'}, unless{' '}
            {differing.length === 1 ? 'it changes' : 'one changes'} first.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
